import React, { ReactElement, useEffect, useState } from 'react';
import { SetField } from './SetField';
import { NamedEntity } from '../../../types/entities/NamedEntity';
import { FieldComponent } from '../../../types/FieldComponent';
import { Status } from '../../../types/entities/Status';
import { compareText } from '../comparators/compareText';
import { SchemaWithClient } from '../../../types/SchemaWithClient';
import { Autocomplete, Button, Chip, Popper, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import ColorizeIcon from '@mui/icons-material/Colorize';
import CircularProgress from '@mui/material/CircularProgress';
import { SlidingPaneAddNew } from '../../Page/SlidingPaneAddNew';
import LargePopper from '../assets/LargePopper';
import AddIcon from "@mui/icons-material/Add";

type Props<T extends NamedEntity> = {
  onChange: (value: T) => void;
  schema: any;
  defaultValue: any;
  forcedValue: any;
  disabled: boolean;
  isMissing: boolean;
  accordingToValue: any;
};

export const SPSelectWithOutAutocomplete = <T extends NamedEntity>({
  onChange,
  defaultValue,
  forcedValue,
  disabled,
  isMissing,
  schema,
  accordingToValue,
}: Props<T>): ReactElement | null => {
  const [addingEntity, setAddingEntity] = useState(false);
  const [selectOptions, setSelectOptions] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const [currentState, setCurrentState] = useState<any>({
    value: null,
    send: false,
  });

  useEffect(() => {
    if (
      typeof defaultValue !== undefined &&
      defaultValue !== null &&
      defaultValue !== undefined
    ) {
      setSelectOptions([defaultValue]);
    }
  }, []);

  useEffect(() => {
    if (defaultValue !== undefined) {
      setCurrentState({
        value: defaultValue,
        send: false,
      });
    }
  }, [defaultValue]);

  useEffect(() => {
    if (currentState.send) {
      onChange(currentState.value);
    }
  }, [currentState]);

  const fetchSelectOptions = async (
    searchText: string,
    columnName?: string,
    row?: any
  ) => {
    setLoading(true);

    let string: any = {
      //searchValue: searchText,
    };

    /* if (mainSchema && mainSchema.name === 'Service Reservations') {
       if (column?.name === 'Activity Reservation') {
         string.withName = true;
         if (allValue?.date) {
           string.activityReservationDate = allValue?.date;
         }
       }

       if (column?.name === 'Service') {
         string.activityReservationActivity = JSON.stringify(
           allValue?.activity_reservation?.activity
         );
       }
     }*/

    if (typeof accordingToValue === 'object') {
      string.accordingToLabel = accordingToValue?.name;
      string.accordingToId = accordingToValue?.id;
      string.accordingToValue = accordingToValue?.value;
      string.withName = true;
    }
    const searchParams = new URLSearchParams(string);

    schema.client.readAll(1, searchParams).then((result: any) => {
      result.data.unshift({ id: 0, name: '', isButton: true });
      setSelectOptions(result.data);
      setLoading(false);
    });
  };

  const handleOnChange = (event: any, value: any) => {
    if (value?.isButton) {
      setAddingEntity(true);
    } else {
      setCurrentState({
        value: value,
        send: true,
      });
    }
  };

  const CustomPopper = function (props: any) {
    return (
      <>
        <div>
          <Popper
            {...props}
            sx={{
              '& .MuiPaper-root': {
                boxShadow: '0 4px 10px -1px rgb(17 20 24 / 20%)',
              },
            }}
            style={{
              width: 'fit-content',
              maxWidth: '300px',
            }}
            placement="bottom-start"
          />
        </div>
      </>
    );
  };

  return (
    <>
      <Autocomplete
        fullWidth={true}
        PopperComponent={LargePopper}
        size="small"
        loading={loading}
        options={selectOptions}
        renderOption={(props: any, option: any) => {
          return (
            <Box
              component="li"
              sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
              {...props}
            >
              {option?.isButton && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  size={'small'}
                  fullWidth
                >
                  Add new
                </Button>
              )}
              {option?.color && typeof option?.color === 'string' ? (
                <Chip
                  style={{
                    background: `#${option?.color}`,
                    color: '#fff',
                  }}
                  icon={
                    <ColorizeIcon color={'disabled'} sx={{ color: 'white' }} />
                  }
                  label={`#${option.name}`}
                />
              ) : (
                option.name
              )}
            </Box>
          );
        }}
        getOptionLabel={(option) => option?.name}
        isOptionEqualToValue={(option, value) => {
          if (!option?.id) return false;
          return option.id === value.id;
        }}
        value={currentState.value}
        onOpen={() => {
          fetchSelectOptions('');
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              size="small"
              InputLabelProps={{
                style: {
                  fontSize: 13,
                  paddingTop: 2,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          );
        }}
        onChange={(event, newValue) => {
          handleOnChange(event, newValue);
        }}
        disabled={disabled}
      />
      {addingEntity && (
        <SlidingPaneAddNew
          schema={schema}
          mainSchema={schema}
          id={'new'}
          hasSlidingPane={false}
          onSubmit={(value: any) => {
            if (value) {
              setCurrentState({
                value: value,
                send: true,
              });
              setAddingEntity(false);
            }
          }}
          propertiesFromTable={{}}
          row={{}}
          onClose={(value) => {
            setAddingEntity(false);
          }}
        />
      )}
    </>
  );
};

SPSelectWithOutAutocomplete.tableAdapter = <
  T extends NamedEntity = NamedEntity
>(
  schema: SchemaWithClient<T>
) => {
  const Field: FieldComponent<T, T[]> = ({
    onChange,
    isActive: active,
    defaultValue,
    forcedValue,
    disabled,
    isMissing,
    accordingToValue,
  }) => {
    return (
      <SPSelectWithOutAutocomplete
        onChange={onChange}
        defaultValue={defaultValue}
        forcedValue={forcedValue}
        disabled={!!disabled}
        isMissing={!!isMissing}
        schema={schema}
        accordingToValue={accordingToValue}
      />
    );
  };

  Field.includes = (query = [], value) =>
    value
      ? query.length === 0 || query.some((item) => value.id === item.id)
      : false;
  Field.comparator = (one, other) => compareText(one?.name, other?.name);
  Field.FilterRenderer = SetField.tableAdapter(schema).FilterRenderer;

  return Field;
};

function isStatusValue(value: NamedEntity): value is Status {
  if (typeof value === 'string' || value === null) {
    return false;
  }
  return 'color' in value;
}

function isStatusLamp(value: NamedEntity): value is Status {
  return 'status_lamp' in value;
}
